import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { z } from '../zod';
import { FORM_LAYOUT_FIELD_ORDER_RULE } from '../page/formLayout';

const FieldOptionValidationSchema = z.object({
  id: ObjectIdStringValidationSchema,
  label: z.string().meta({
    description: 'Display label shown to customers for this option.',
    example: 'Label',
  }),
  value: z.string().nullish().meta({
    description: 'Optional internal value for this option. Defaults to the label when omitted.',
    example: 'value',
  }),
});

export const FieldResponseValidationSchema = z
  .object({
    id: z.string().meta({
      description: 'Unique identifier for the field.',
      example: '507f1f77bcf86cd799439011',
    }),
    label: z.string().meta({
      description: 'Display label shown to customers for this field.',
      example: 'Company Name',
    }),
    description: z.string().nullish().meta({
      description: 'Optional helper text shown below the field label.',
      example: 'We will use this on your invoice.',
    }),
    placeholder: z.string().nullish().meta({
      description: 'Placeholder hint shown inside the input before a value is entered.',
      example: 'Enter your company name',
    }),
    element: z.string().meta({
      description: 'Input control used to render this field at checkout.',
      example: 'text',
    }),
    type: z.string().nullish().meta({
      description:
        'Used to store field data in a predictable manner. Please note that you must use Customer Name and Shipping Address if you wish to use any of the shipping address data types.',
      example: 'company-name',
    }),
    options: z.array(FieldOptionValidationSchema).nullish().meta({
      description:
        'Selectable options. Applies to `select`, `multi-select` and `multiple-choice` elements.',
    }),
    layout: z.enum(['dropdown', 'list']).nullish().meta({
      description:
        'How a multi-select field is drawn at checkout: `dropdown` (the default) or `list`, a list of checkboxes. Ignored on other elements.',
    }),
    required: z.boolean().meta({
      description: 'Whether this field must be completed before checkout can continue.',
      example: true,
    }),
    order: z.number().meta({
      description: `Position of the field among the page's fields, from 0. ${FORM_LAYOUT_FIELD_ORDER_RULE}`,
      example: 0,
    }),
    reference: z.string().nullish().meta({
      description:
        "Stable key that exports, Zapier, Google Sheets and webhooks use for this field's answers. Not updated when the label changes.",
      example: 'custom_field_1',
    }),
    disabled: z.boolean().nullish().meta({
      description:
        'Whether this field is switched off. A disabled field keeps its settings but is removed from the page entirely.',
      example: false,
    }),
    hidden: z.boolean().nullish().meta({
      description: 'Whether this field is hidden from customers at checkout.',
      example: false,
    }),
    defaultValue: z
      .object({
        enabled: z.boolean().nullish(),
        value: z.string().nullish(),
        values: z.array(z.string()).nullish(),
      })
      .nullish()
      .meta({
        description: 'Default value settings for the field.',
      }),
    showHideLogic: z
      .object({
        enabled: z.boolean().nullish(),
        comparison: z.enum(['is', 'is_not', 'is_empty', 'is_not_empty']).nullish(),
        value: z.string().nullish(),
        element: z
          .object({
            elementTitle: z.string().nullish(),
            elementType: z.enum(['field']).nullish(),
            elementId: z.string().nullish(),
          })
          .nullish(),
      })
      .nullish()
      .meta({
        description: 'Conditional visibility rules for this field.',
      }),
    minValue: z
      .object({
        enabled: z.boolean().nullish(),
        value: z.string().nullish(),
      })
      .nullish()
      .meta({
        description: 'Minimum allowed value rules for this field.',
      }),
    maxValue: z
      .object({
        enabled: z.boolean().nullish(),
        value: z.string().nullish(),
      })
      .nullish()
      .meta({
        description: 'Maximum allowed value rules for this field.',
      }),
    limitAllowedCountries: z
      .object({
        enabled: z.boolean().nullish(),
        countries: z.array(z.string()).nullish(),
      })
      .nullish()
      .meta({
        description: 'Country restriction settings for fields that collect country data.',
      }),
    showSelectedDialCode: z.boolean().nullish().meta({
      description: 'Whether to display the selected international dial code in phone inputs.',
    }),
    collect: z.enum(['billing', 'billing_and_shipping']).nullish().meta({
      description:
        'Whether to collect only a billing address or both billing and shipping. Applies to `address` elements.',
      example: 'billing',
    }),
    collectShippingPhone: z.boolean().nullish().meta({
      description:
        'Whether to collect a phone number alongside the shipping address. Applies to `address` elements when `collect` is `billing_and_shipping`.',
      example: false,
    }),
    limitShippingCountries: z
      .object({
        enabled: z.boolean().nullish(),
        countries: z.array(z.string()).nullish(),
      })
      .nullish()
      .meta({
        description:
          'Restrict the shipping country list. Applies to `address` elements when `collect` is `billing_and_shipping`.',
      }),
    limitBillingCountries: z
      .object({
        enabled: z.boolean().nullish(),
        countries: z.array(z.string()).nullish(),
      })
      .nullish()
      .meta({
        description: 'Restrict the billing country list. Applies to `address` elements.',
      }),
    defaultCountry: z.string().nullish().meta({
      description: 'ISO 3166-1 alpha-2 country code used as the default for `address` elements.',
      example: 'US',
    }),
    createdAt: z.string().meta({
      description: 'ISO-8601 timestamp for when the field was created.',
      example: '2024-01-15T10:30:00.000Z',
    }),
    updatedAt: z.string().meta({
      description: 'ISO-8601 timestamp for when the field was last updated.',
      example: '2024-01-15T10:30:00.000Z',
    }),
  })
  .meta({
    id: 'Field',
  });

export const FieldDetailResponseValidationSchema = z
  .object({
    data: FieldResponseValidationSchema,
  })
  .meta({ description: 'A single custom field on a checkout page, form, or event.' });

export const DeleteFieldResponseValidationSchema = z.object({
  data: z.object({
    success: z.boolean(),
    message: z.string(),
  }),
});
